// Bump CONSENT_VERSION when ANY of the following occur:
//   • A new third-party analytics, ads, or tracking tool is added
//   • Data starts being shared with a new recipient
//   • The privacy policy changes in a way that affects user rights
//   • An existing tool's data processing terms change materially
// Do NOT bump for: bug fixes, UI changes, perf work, or backend-only changes.
// Process: (1) increment here  (2) update Strapi cookie-alert description  (3) deploy
const CONSENT_VERSION = 1;
const COOKIE_NAME = "cookieConsent";
const LEGACY_COOKIE_NAME = "acceptCookies";
const COOKIE_DAYS = 365;

export interface ConsentState {
  analytics: boolean;
  marketing: boolean;
  version: number;
  timestamp: number;
}

function setCookieRaw(name: string, value: string, days: number): void {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  const secure = typeof location !== "undefined" && location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax${secure}`;
}

function getCookieRaw(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split("=").slice(1).join("=")) : null;
}

export function getConsent(): ConsentState | null {
  if (typeof document === "undefined") return null;
  const raw = getCookieRaw(COOKIE_NAME);
  if (raw) {
    try {
      return JSON.parse(raw) as ConsentState;
    } catch {
      return null;
    }
  }
  // Migrate legacy acceptCookies=true cookie
  if (getCookieRaw(LEGACY_COOKIE_NAME) === "true") {
    return setConsent(true, true);
  }
  return null;
}

export function setConsent(analytics: boolean, marketing: boolean): ConsentState {
  const state: ConsentState = {
    analytics,
    marketing,
    version: CONSENT_VERSION,
    timestamp: Date.now(),
  };
  setCookieRaw(COOKIE_NAME, JSON.stringify(state), COOKIE_DAYS);
  return state;
}

// Returns true only if the user has responded to the *current* policy version.
// Bump CONSENT_VERSION whenever the privacy policy changes to re-surface the banner.
export function hasResponded(): boolean {
  const consent = getConsent();
  return consent !== null && consent.version >= CONSENT_VERSION;
}

export { CONSENT_VERSION };
